import { Resource } from 'src/app/shared/interfaces'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
import type { PayMethodType } from '../sale/payment/payment'
import type {
  CancellationPolicy as SharedCancellationPolicy,
  CancellationPolicyValue as SharedCancellationPolicyValue
} from 'src/app/shared/interfaces'

export class ConfigurationOption extends Resource {
  id: number
  key: string
  value: string
  siteId?: number
}

export interface ConfigOptionModule {
  isActive?: boolean
  uploadDocuments?: boolean
  uploadProfilePicture?: boolean
  shop?: boolean
}

export enum ConfigurationOptionKeys {
  DISCOVERY_AREA_CONFIG = 'discovery_area_config',
  CANCELLATION_POLICIES = 'cancellation_policies',
  INVOICE_REPORT_21_ALLOW_CLOSE_EMPLOYEE_BOOKINGS = 'invoice_report_21_allow_close_employee_bookings',
  MEMBER_REGISTRATION_FORM_CONFIG = 'member_registration_form_config'
}

export type CancellationPolicyValue = SharedCancellationPolicyValue

export type CancellationPolicy = SharedCancellationPolicy

export interface ChildRegistrationFormField {
  required?: boolean
}

export interface ChildRegistrationFormConfig {
  middleName?: ChildRegistrationFormField
  lastName?: ChildRegistrationFormField
  birthDate?: ChildRegistrationFormField
}

export interface FamilySettingsWebOption {
  isActive: boolean
  requireChildApproval: boolean
  allowChildren: boolean
  childDefaultCategory: number
  maxMembersPerFamily: number
  childRegistrationForm?: ChildRegistrationFormConfig
}

export interface ScheduleItemsInScheduleSettings {
  showCourses: boolean
  showFullBookings: boolean
  showSlotGeneratedBookings: boolean
  showPotentialActivities: boolean
}

export interface ScheduleFilterSettings {
  showTimeFilter: boolean
  showLocationFilter: boolean
  showMembershipFilter: boolean
  showSupervisorFilter: boolean
}

export interface ScheduleSettingsWebOption {
  itemsInSchedule: ScheduleItemsInScheduleSettings
  scheduleFilter: ScheduleFilterSettings
}

export type CheckoutPaymentBrand =
  | 'apple-pay'
  | 'bancontact'
  | 'google-pay'
  | 'ideal'
  | 'maestro'
  | 'mastercard'
  | 'paypal'
  | 'sepa'
  | 'visa'

export interface LegacyDirectDebitConfig {
  requireFirstPaymentOnline?: boolean
}

export const MEMBER_REGISTRATION_FORM_CONFIG_FIELD_NAMES = [
  'email',
  'password',
  'passwordRepeat',
  'firstName',
  'middleName',
  'lastName',
  'street',
  'houseNr',
  'postbox',
  'city',
  'municipality',
  'region',
  'country',
  'zipcode',
  'language',
  'mobile',
  'phone',
  'gender',
  'category',
  'birthDate',
  'shouldSync',
  'memberNumber',
  'generalTerms',
  'privacyTerms',
  'biometricTerms',
  'organizationId',
  'documents',
  'profilePicture',
  'status',
  'bankAccount',
  'bankAccountHolder',
  'directDebitTerms'
] as const

export type MemberRegistrationFormConfigFieldName =
  (typeof MEMBER_REGISTRATION_FORM_CONFIG_FIELD_NAMES)[number]

export const MEMBER_REVALIDATION_FORM_CONFIG_FIELD_NAMES = [
  'firstName',
  'middleName',
  'lastName',
  'street',
  'houseNr',
  'postbox',
  'city',
  'country',
  'zipcode',
  'municipality',
  'region',
  'language',
  'mobile',
  'phone',
  'gender',
  'category',
  'birthDate',
  'memberNumber',
  'email',
  'generalTerms',
  'privacyTerms',
  'biometricTerms'
] as const satisfies readonly MemberRegistrationFormConfigFieldName[]

export type MemberRevalidationFormConfigFieldName =
  (typeof MEMBER_REVALIDATION_FORM_CONFIG_FIELD_NAMES)[number]

export interface MemberFormConfigField<
  TInputName extends string = MemberRegistrationFormConfigFieldName
> {
  inputName: TInputName
  title_en: string
  title_nl: string
  title_de?: string
  title_fr?: string
  placeholder_en?: string
  placeholder_nl?: string
  placeholder_de?: string
  placeholder_fr?: string
  isVisible: boolean
  isRequired: boolean
  isEditable: boolean
}

export type RegistrationFormFieldConfig =
  MemberFormConfigField<MemberRegistrationFormConfigFieldName>

export type RevalidationFormFieldConfig =
  MemberFormConfigField<MemberRevalidationFormConfigFieldName>

export type MemberFormConfigValueMap<
  TInputName extends string = MemberRegistrationFormConfigFieldName
> = Partial<Record<TInputName, MemberFormConfigField<TInputName>>>

export interface RegistrationCategoryFilterOption {
  name_en: string
  name_nl: string
  name_fr?: string
  name_de?: string
  belongingCategories: number[]
  ordinal?: number | string
}

export interface RegistrationCategoryFilter {
  description_en: string
  description_nl: string
  description_fr?: string
  description_de?: string
  options: RegistrationCategoryFilterOption[]
}

export interface RegistrationCategoryOptionsOption {
  name_en: string
  name_nl: string
  name_fr?: string
  name_de?: string
  optionId: number
  categoryValue: number
  group: string
}

export interface RegistrationCategoryOptions {
  description_en: string
  description_nl: string
  description_fr?: string
  description_de?: string
  options: RegistrationCategoryOptionsOption[]
}

export interface ConfigurationOptionTypedValueMap {
  [ConfigOptionsKeys.FAMILY_SETTINGS]: FamilySettingsWebOption
  [ConfigOptionsKeys.REGISTRATION_CATEGORY_FILTER]: RegistrationCategoryFilter
  [ConfigOptionsKeys.REGISTRATION_CATEGORY_OPTIONS]: RegistrationCategoryOptions
  [ConfigOptionsKeys.MEMBER_REGISTRATION_FORM_CONFIG]: RegistrationFormFieldConfig[]
  [ConfigOptionsKeys.MEMBER_REVALIDATION_FORM_CONFIG]: RevalidationFormFieldConfig[]
  [ConfigOptionsKeys.MEMBER_REVALIDATION_SSO_FORM_CONFIG]: RevalidationFormFieldConfig[]
  [ConfigOptionsKeys.NOTIFICATIONS_AGREEMENTS_DISPLAY]: boolean
  [ConfigOptionsKeys.NOTIFICATIONS_AGREEMENTS_DISPLAY_OFFERS]: boolean
  [ConfigOptionsKeys.NOTIFICATIONS_AGREEMENTS_DISPLAY_NEWSLETTERS]: boolean
  [ConfigOptionsKeys.ACTIVE_ALLOWED_PAY_METHODS]: PayMethodType[]
  [ConfigOptionsKeys.CHECKOUT_PAYMENT_BRANDS]: CheckoutPaymentBrand[]
  [ConfigOptionsKeys.LEGACY_DIRECT_DEBIT_CONFIG]:
    | LegacyDirectDebitConfig
    | boolean
  [ConfigOptionsKeys.MULTIDAY_ENABLED]: boolean
}
